/**
 * Edit Field Modal Component
 *
 * Modal for editing builder form field properties (placeholder text, alignment, etc.)
 */

import React, { useState } from 'react';
import styles from './EditFieldModal.module.scss';

export interface FieldData {
  id: string;
  type: 'textfield' | 'checkbox' | 'freetext';
  placeholder?: string;
  fieldName?: string;
  width: number;
  height: number;
  multiline?: boolean;
  horizontalAlign?: 'left' | 'center' | 'right';
  fontSize?: number;
  fontColor?: string;
  borderColor?: string;
  backgroundColor?: string;
}

export interface EditFieldModalProps {
  field: FieldData;
  onSave: (updates: Partial<FieldData>) => void;
  onClose: () => void;
}

export const EditFieldModal: React.FC<EditFieldModalProps> = ({
  field,
  onSave,
  onClose,
}) => {
  const [placeholder, setPlaceholder] = useState(field.placeholder || '');
  // Hide auto-generated names (`field_…`) from the user — they're solo markers.
  const initialFieldName =
    field.fieldName && !field.fieldName.startsWith('field_') ? field.fieldName : '';
  const [fieldName, setFieldName] = useState(initialFieldName);
  const [multiline, setMultiline] = useState(field.multiline || false);
  const [horizontalAlign, setHorizontalAlign] = useState<'left' | 'center' | 'right'>(field.horizontalAlign || 'left');
  const [fontSize, setFontSize] = useState(field.fontSize || 12);
  const [fontColor, setFontColor] = useState(field.fontColor || '#000000');
  const [borderColor, setBorderColor] = useState(field.borderColor || 'transparent');
  const [backgroundColor, setBackgroundColor] = useState(field.backgroundColor || '#ffffff');

  const handleSave = () => {
    const trimmed = fieldName.trim();
    onSave({
      placeholder,
      // Empty → leave existing fieldName alone (keeps auto-generated name intact).
      // Non-empty → new link key. Treat as a controlled change.
      ...(trimmed ? { fieldName: trimmed } : {}),
      multiline,
      horizontalAlign,
      fontSize,
      fontColor,
      borderColor,
      backgroundColor,
    });
    onClose();
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>
            Edit {field.type === 'freetext' ? 'Free Text' : field.type === 'textfield' ? 'Text Field' : 'Checkbox'}
          </h3>
          <button className={styles.closeButton} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.content}>
          {(field.type === 'textfield' || field.type === 'checkbox') && (
            <div className={styles.formGroup}>
              <label className={styles.label}>Field Name (link key)</label>
              <input
                type="text"
                className={styles.input}
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                placeholder="Leave blank for a solo field"
              />
              <small className={styles.helpText}>
                Fields sharing the same name sync together (e.g. same field repeated on every page).
              </small>
            </div>
          )}
          {(field.type === 'textfield' || field.type === 'freetext') && (
            <>
              <div className={styles.formGroup}>
                <label className={styles.label}>Placeholder Text</label>
                <input
                  type="text"
                  className={styles.input}
                  value={placeholder}
                  onChange={(e) => setPlaceholder(e.target.value)}
                  placeholder="Enter placeholder text"
                />
              </div>

              <div className={styles.formGroup}>
                <label className={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={multiline}
                    onChange={(e) => setMultiline(e.target.checked)}
                    className={styles.checkbox}
                  />
                  <span>Multiline</span>
                </label>
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>Horizontal Alignment</label>
                <div className={styles.alignmentButtons}>
                  <button
                    type="button"
                    className={`${styles.alignButton} ${horizontalAlign === 'left' ? styles.active : ''}`}
                    onClick={() => setHorizontalAlign('left')}
                    title="Align left"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M2 3h12v2H2zM2 7h8v2H2zM2 11h12v2H2z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`${styles.alignButton} ${horizontalAlign === 'center' ? styles.active : ''}`}
                    onClick={() => setHorizontalAlign('center')}
                    title="Align center"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M1 3h14v2H1zM3 7h10v2H3zM1 11h14v2H1z"/>
                    </svg>
                  </button>
                  <button
                    type="button"
                    className={`${styles.alignButton} ${horizontalAlign === 'right' ? styles.active : ''}`}
                    onClick={() => setHorizontalAlign('right')}
                    title="Align right"
                  >
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M2 3h12v2H2zM6 7h8v2H6zM2 11h12v2H2z"/>
                    </svg>
                  </button>
                </div>
              </div>

              <div className={styles.fontRow}>
                <div className={styles.fontSizeGroup}>
                  <label className={styles.label}>Font Size (px)</label>
                  <input
                    type="number"
                    className={styles.fontSizeInput}
                    value={fontSize}
                    onChange={(e) => {
                      const num = parseInt(e.target.value) || 12;
                      setFontSize(num);
                    }}
                    onBlur={(e) => {
                      const num = parseInt(e.target.value) || 12;
                      setFontSize(Math.max(8, Math.min(72, num)));
                    }}
                    min="8"
                    max="72"
                  />
                </div>

                <div className={styles.fontColorGroup}>
                  <label className={styles.label}>Font Color</label>
                  <div className={styles.colorPickerWrapper}>
                    <input
                      type="color"
                      className={styles.colorPicker}
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                    />
                    <input
                      type="text"
                      className={styles.colorInput}
                      value={fontColor}
                      onChange={(e) => setFontColor(e.target.value)}
                      placeholder="#000000"
                    />
                  </div>
                </div>
              </div>

              {field.type === 'freetext' && (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Border Color</label>
                    <div className={styles.colorPickerWrapper}>
                      <input
                        type="color"
                        className={styles.colorPicker}
                        value={borderColor === 'transparent' ? '#000000' : borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                      />
                      <input
                        type="text"
                        className={styles.colorInput}
                        value={borderColor}
                        onChange={(e) => setBorderColor(e.target.value)}
                        placeholder="transparent"
                      />
                    </div>
                  </div>

                  <div className={styles.formGroup}>
                    <label className={styles.label}>Background Color</label>
                    <div className={styles.colorPickerWrapper}>
                      <input
                        type="color"
                        className={styles.colorPicker}
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                      />
                      <input
                        type="text"
                        className={styles.colorInput}
                        value={backgroundColor}
                        onChange={(e) => setBackgroundColor(e.target.value)}
                        placeholder="#ffffff"
                      />
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button className={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button className={styles.saveButton} onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>
    </div>
  );
};
